import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMto } from '../../context/MtoContext';
import LocationList from '../LocationList';
import { clearConnections } from '../../Map/ConnectionRenderer';
import DemandInput from '../SetupMode/DemandInput';
import ModeHelpTooltip from './ModeHelpTooltip';
import './connectionMode.css';

// Transport mode configuration
const TRANSPORT_MODES = {
  electric_motorcycle: {
    id: 'electric_motorcycle',
    name: 'Bike',
    color: '#00ff00'
  },
  truck: {
    id: 'truck',
    name: 'Truck',
    color: '#ff4444'
  },
  railway: {
    id: 'railway',
    name: 'Train',
    color: '#4444ff'
  },
  shipping: {
    id: 'shipping',
    name: 'Ship',
    color: '#44aaff'
  }
};

/**
 * //Connection Mode Component
 * Allows users to select transport modes and create connections between two points
 */
export default function ConnectionMode({ 
  locations, 
  startId,
  endId,
  setLocations, 
  setStartId,
  setEndId,
  setMapPointSelect,
  currentModeRef,
  demand,
  setDemand,
  modeCapacities,
  setModeCapacities      
}) {
  // Connection state
  const [selectedTransportMode, setSelectedTransportMode] = useState(null);
  const [connectionPoints, setConnectionPoints] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDurationInput, setShowDurationInput] = useState(false);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [durationInput, setDurationInput] = useState('');
  const [showImportToast, setShowImportToast] = useState(false);
  const [importToastMessage, setImportToastMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Refs to hold current state values for callback
  const isConnectingRef = useRef(isConnecting);
  const selectedTransportModeRef = useRef(selectedTransportMode);
  const lastConnectionTimeRef = useRef(0);

  // Update refs when state changes
  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  useEffect(() => {
    selectedTransportModeRef.current = selectedTransportMode;
  }, [selectedTransportMode]);

  // Get connection state and map reference from context
  const { connections, setConnections, mapRef } = useMto();

  // Check if there are connections with missing capacities
  const getMissingCapacities = useCallback(() => {
    if (!connections || connections.length === 0 || demand === 0) {
      return [];
    }
    
    // Get unique transport modes used in connections
    const usedModes = [...new Set(connections.map(conn => conn.transportMode))];
    
    // Filter modes that have no capacity set
    const missingModes = usedModes.filter(modeId => {
      const capacity = modeCapacities[modeId];
      return !capacity || capacity <= 0;
    });
    
    return missingModes.map(modeId => TRANSPORT_MODES[modeId]?.name || modeId);
  }, [connections, demand, modeCapacities]);

  const missingCapacityModes = getMissingCapacities();

  const fileInputRef = useRef(null);

  const resetConnectionState = useCallback(() => {
    setConnectionPoints([]);
    setIsConnecting(false);
  }, []);

  // Handle capacity input change
  const handleCapacityChange = useCallback((modeId, value) => {
    const numValue = parseFloat(value) || 0;
    setModeCapacities(prev => ({
      ...prev,
      [modeId]: numValue >= 0 ? numValue : 0
    }));
  }, [setModeCapacities]);

  const handleTransportModeSelect = useCallback((mode) => {
    if (selectedTransportMode === mode && isConnecting) {
      setSelectedTransportMode(null);
      resetConnectionState();
    } else {
      setSelectedTransportMode(mode);
      setIsConnecting(true);
      setConnectionPoints([]);
    }
  }, [selectedTransportMode, isConnecting, resetConnectionState]);



  // Set map click callback
  useEffect(() => {
    console.log('Setting map point callback');
    console.log('Is connecting:', isConnecting);
    
    if (isConnecting && setMapPointSelect) {
      console.log('Setting point select callback');
      // Create a stable callback that doesn't depend on connectionPoints
      setMapPointSelect((point) => {
        console.log('Point selected:', point);
        console.log('Connecting:', isConnectingRef.current);
        console.log('Transport mode:', selectedTransportModeRef.current);
        
        if (!isConnectingRef.current || !selectedTransportModeRef.current) {
          console.log('Ignoring point - not connecting or no transport mode');
          return;
        }

        setConnectionPoints(prevPoints => {
          const newConnectionPoints = [...prevPoints, point];
          console.log('Updated connection points:', newConnectionPoints);

          // If two points are selected, create connection
          if (newConnectionPoints.length === 2) {
            const now = Date.now();
            if (now - lastConnectionTimeRef.current < 500) {
              return newConnectionPoints;
            }
            lastConnectionTimeRef.current = now;
            
            const currentTransportMode = selectedTransportModeRef.current;
            const transportModeConfig = TRANSPORT_MODES[currentTransportMode];
            
            if (!transportModeConfig) {
              console.error('Invalid transport mode:', currentTransportMode);
              return newConnectionPoints;
            }
            
            const newConnection = {
              id: `connection_${Date.now()}`,
              transportMode: currentTransportMode,
              points: newConnectionPoints,
              color: transportModeConfig.color,
              duration: null
            };
            
            const existingConnection = connections.find(conn => 
              conn.transportMode === newConnection.transportMode &&
              conn.points.length === 2 &&
              conn.points[0].id === newConnection.points[0].id &&
              conn.points[1].id === newConnection.points[1].id
            );
            
            if (existingConnection) {
              setConnectionPoints([]);
              setIsConnecting(false);
              return newConnectionPoints;
            }
            
            setPendingConnection(newConnection);
            setShowDurationInput(true);
            setDurationInput('');
            setConnectionPoints([]);
            setIsConnecting(false);
          }
          
          return newConnectionPoints;
        });
      });
    } else if (setMapPointSelect) {
      setMapPointSelect(null);
    }
  }, [isConnecting, selectedTransportMode, setMapPointSelect]);

  // Clear all connections
  const clearAllConnections = useCallback(() => {
    setConnections([]);
    resetConnectionState();
    
    // Force clear connections from map immediately
    if (mapRef && mapRef.current) {
      clearConnections(mapRef.current, true, true);
    }
  }, [resetConnectionState, mapRef]);

  // Remove single connection
  const removeConnection = useCallback((connectionId) => {
    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
  }, []);

  // Export connections to CSV
  const exportToCSV = useCallback(() => {
    if (connections.length === 0) {
      alert('No connections to export');
      return;
    }

    // Create CSV header
    const headers = ['ID', 'Latitude', 'Longitude', 'start', 'end', 'mode', 'duration_hr'];
    
    // Create CSV rows
    const rows = [];
    
    // Create a mapping from original location ID to simple numeric ID
    const locationIdMap = new Map();
    locations.forEach((location, index) => {
      const simpleId = (index + 1).toString(); // 1, 2, 3, ...
      locationIdMap.set(location.id, simpleId);
    });
    
    // Add location points first with simple numeric IDs
    locations.forEach((location, index) => {
      const simpleId = (index + 1).toString();
      rows.push([
        simpleId,
        location.latitude,
        location.longitude,
        '', // start
        '', // end
        '', // mode
        ''  // duration_hr
      ]);
    });
    
    // Add connection data with simple numeric IDs
    connections.forEach(connection => {
      const startId = locationIdMap.get(connection.points[0]?.id) || '';
      const endId = locationIdMap.get(connection.points[1]?.id) || '';
      
      rows.push([
        '', // ID (empty for connection rows)
        '', // Latitude (empty for connection rows)
        '', // Longitude (empty for connection rows)
        startId,
        endId,
        connection.transportMode,
        connection.duration || ''
      ]);
    });

    // Convert to CSV string
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `mto_connections_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [connections, locations]);

  // Export connections to GeoJSON
  const exportToGeoJSON = useCallback(() => {
    if (connections.length === 0) {
      alert('No connections to export');
      return;
    }

    // Create a mapping from original location ID to simple numeric ID
    const locationIdMap = new Map();
    locations.forEach((location, index) => {
      const simpleId = (index + 1).toString(); // 1, 2, 3, ...
      locationIdMap.set(location.id, simpleId);
    });

    // Create GeoJSON structure
    const geoJson = {
      type: 'FeatureCollection',
      features: [
        // Add location points as features with simple numeric IDs
        ...locations.map((location, index) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [location.longitude, location.latitude]
          },
          properties: {
            id: (index + 1).toString(),
            name: location.name,
            type: 'location'
          }
        })),
        // Add connections as line features with simple numeric IDs
        ...connections.map(connection => ({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [connection.points[0].longitude, connection.points[0].latitude],
              [connection.points[1].longitude, connection.points[1].latitude]
            ]
          },
          properties: {
            id: connection.id,
            transportMode: connection.transportMode,
            startPointId: locationIdMap.get(connection.points[0]?.id) || '',
            endPointId: locationIdMap.get(connection.points[1]?.id) || '',
            duration: connection.duration,
            color: connection.color,
            type: 'connection'
          }
        }))
      ]
    };

    // Download GeoJSON file
    const blob = new Blob([JSON.stringify(geoJson, null, 2)], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `mto_connections_${new Date().toISOString().split('T')[0]}.geojson`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [connections, locations]);

  // Parse CSV content
  const parseCSV = useCallback((csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    const expectedHeaders = ['ID', 'Latitude', 'Longitude', 'start', 'end', 'mode', 'duration_hr'];
    
    // Validate headers (flexible matching)
    const missingHeaders = expectedHeaders.filter(expected => 
      !headers.some(header => header.toLowerCase() === expected.toLowerCase())
    );
    
    if (missingHeaders.length > 0) {
      throw new Error(`CSV missing required headers: ${missingHeaders.join(', ')}`);
    }

    const newLocations = [];
    const newConnections = [];
    const locationMap = new Map();

    // Create header mapping for flexible parsing
    const headerMap = {};
    expectedHeaders.forEach(expected => {
      const foundHeader = headers.find(header => header.toLowerCase() === expected.toLowerCase());
      if (foundHeader) {
        headerMap[expected] = foundHeader;
      }
    });

    // First pass: collect all locations
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // If this row has location data (ID, Latitude, Longitude)
      const idValue = row[headerMap.ID] || row.ID;
      const latValue = row[headerMap.Latitude] || row.Latitude;
      const lngValue = row[headerMap.Longitude] || row.Longitude;
      
      if (idValue && latValue && lngValue) {
        const location = {
          id: idValue,
          name: idValue,
          latitude: parseFloat(latValue),
          longitude: parseFloat(lngValue),
          type: 'normal'
        };
        
        if (!isNaN(location.latitude) && !isNaN(location.longitude)) {
          newLocations.push(location);
          locationMap.set(location.id, location);
        }
      }
    }

    // Second pass: process connections
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // If this row has connection data (start, end, mode, duration)
      const startValue = row[headerMap.start] || row.start;
      const endValue = row[headerMap.end] || row.end;
      const modeValue = row[headerMap.mode] || row.mode;
      const durationValue = row[headerMap.duration_hr] || row.duration_hr;
      
      if (startValue && endValue && modeValue) {
        const duration = durationValue ? parseFloat(durationValue) : null;
        
        // Create connection even if duration is invalid or missing
        // Find transport mode color
        const transportModeKey = Object.keys(TRANSPORT_MODES).find(key => 
          TRANSPORT_MODES[key].name.toLowerCase() === modeValue.toLowerCase() ||
          key === modeValue
        );
        
        // Find the actual location objects for the connection points
        const startLocation = locationMap.get(startValue);
        const endLocation = locationMap.get(endValue);
        
        if (startLocation && endLocation) {
          const connection = {
            id: `connection_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            transportMode: transportModeKey || 'truck',
            points: [startLocation, endLocation],
            duration: (!isNaN(duration) && duration > 0) ? duration : null,
            color: TRANSPORT_MODES[transportModeKey || 'truck'].color
          };
          
          newConnections.push(connection);
        }
      }
    }

    return { locations: newLocations, connections: newConnections };
  }, []);

  // Handle file import
  const handleFileImport = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        
        if (file.name.toLowerCase().endsWith('.csv')) {
          const parsedData = parseCSV(content);
          
          // Update locations and connections
          setLocations(parsedData.locations);
          setConnections(parsedData.connections);

          showImportSuccess(`Successfully imported ${parsedData.locations.length} locations and ${parsedData.connections.length} connections`);
        } else {
          throw new Error('Only CSV files are supported for import');
        }
        
      } catch (error) {
        console.error('Import error:', error);
        alert(`Import failed: ${error.message}`);
      }
    };

    reader.readAsText(file);
    // Reset file input
    event.target.value = '';
  }, [parseCSV, setLocations, setConnections]);

  // Trigger file import
  const triggerFileImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle duration input submission
  const handleDurationSubmit = useCallback(() => {
    if (!pendingConnection || !durationInput.trim()) {
      alert('Please enter a valid duration');
      return;
    }

    const duration = parseFloat(durationInput);
    if (isNaN(duration) || duration <= 0) {
      alert('Please enter a valid positive number for duration');
      return;
    }

    const finalConnection = {
      ...pendingConnection,
      duration: duration
    };

    setConnections(prev => [...prev, finalConnection]);
    
    setShowDurationInput(false);
    setPendingConnection(null);
    setDurationInput('');
    setSelectedTransportMode(null);
  }, [pendingConnection, durationInput]);

  const handleDurationCancel = useCallback(() => {
    setShowDurationInput(false);
    setPendingConnection(null);
    setDurationInput('');
    setSelectedTransportMode(null);
  }, []);

  // Show import toast message
  const showImportSuccess = useCallback((message) => {
    setImportToastMessage(message);
    setShowImportToast(true);
    
    // Hide toast after 1 second
    setTimeout(() => {
      setShowImportToast(false);
    }, 1000);
  }, []);

  const toggleHelp = () => setShowHelp(!showHelp);

  return (
    <div className="connection-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Connection Mode</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Connection Mode"
          >
            ?
          </button>
        </div>
        <p>Click a transport mode to start connecting, then click two points on the map. Click the same transport mode again to cancel.</p>
      </div>

      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}

      {/* Demand input */}
      <DemandInput 
        demand={demand}
        setDemand={setDemand}
      />

      {/* Transport mode selection */}
      <div className="transport-mode-selector">
        <h4>Select Transport Mode</h4>
        <div className="transport-modes">
          {Object.values(TRANSPORT_MODES).map(mode => {
            const isSelected = selectedTransportMode === mode.id;
            const isCurrentlyConnecting = isSelected && isConnecting;
            const isCapacityEnabled = demand > 0;
            
            return (
              <div key={mode.id} className="transport-mode-card">
                <button
                  className={`transport-mode-btn ${isSelected ? 'selected' : ''} ${isCurrentlyConnecting ? 'connecting' : ''}`}
                  onClick={() => handleTransportModeSelect(mode.id)}
                  style={{ 
                    borderColor: isSelected ? mode.color : '#ccc',
                    backgroundColor: isSelected ? `${mode.color}20` : 'transparent'
                  }}
                >
                  <span className="transport-name">{mode.name}</span>
                  {isCurrentlyConnecting && (
                    <span className="connection-progress">
                      {connectionPoints.length}/2
                    </span>
                  )}
                  <div 
                    className="transport-color-indicator" 
                    style={{ backgroundColor: mode.color }}
                  ></div>
                </button>
                
                {/* Capacity input - only shown when demand > 0 */}
                {isCapacityEnabled && (
                  <div className="mode-capacity-input-wrapper">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={modeCapacities[mode.id] || ''}
                      onChange={(e) => handleCapacityChange(mode.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder={`${mode.name} Capacity`}
                      className="mode-capacity-input"
                      title={`${mode.name} capacity (e.g., tons or passengers)`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Import/Export controls */}
      <div className="import-export-controls">
        <h4>Import/Export</h4>
        <div className="import-export-buttons">
          <button 
            className="import-btn"
            onClick={triggerFileImport}
          >
            Import
          </button>
          <button 
            className="export-csv-btn"
            onClick={exportToCSV}
            disabled={connections.length === 0}
          >
            Export CSV
          </button>
          <button 
            className="export-geojson-btn"
            onClick={exportToGeoJSON}
            disabled={connections.length === 0}
          >
            Export GeoJSON
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.geojson,.json"
          onChange={handleFileImport}
          style={{ display: 'none' }}
        />
      </div>


      {/* Duration input dialog */}
      {showDurationInput && pendingConnection && (
        <div className="duration-input-dialog">
          <div className="duration-dialog-content">
            <h4>Set Connection Duration</h4>
            <p>
              Connection: {pendingConnection.points[0]?.name} → {pendingConnection.points[1]?.name}
            </p>
            <p>Transport: {TRANSPORT_MODES[pendingConnection.transportMode]?.name}</p>
            
            <div className="duration-input-group">
              <label htmlFor="duration-input">Duration (hours):</label>
              <input
                id="duration-input"
                type="number"
                min="0"
                step="0.1"
                value={durationInput}
                onChange={(e) => setDurationInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDurationSubmit();
                  }
                }}
                placeholder="Enter duration in hours"
                className="duration-input"
                autoFocus
              />
            </div>
            
            <div className="duration-dialog-actions">
              <button 
                className="duration-submit-btn"
                onClick={handleDurationSubmit}
              >
                Confirm
              </button>
              <button 
                className="duration-cancel-btn"
                onClick={handleDurationCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capacity warning - shown when demand > 0 and some modes have no capacity set */}
      {missingCapacityModes.length > 0 && (
        <div className="capacity-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <strong>Capacity Not Set</strong>
            <p>Please set capacity for: <strong>{missingCapacityModes.join(', ')}</strong></p>
          </div>
        </div>
      )}

      {/* Created connections list */}
      {connections.length > 0 && (
        <div className="connections-list">
          <div className="connections-header">
            <h4>Created Connections ({connections.length})</h4>
            <button 
              className="clear-all-btn"
              onClick={clearAllConnections}
            >
              Clear All
            </button>
          </div>
          
          <div className="connections">
            {Object.values(TRANSPORT_MODES).map(mode => {
              const modeConnections = connections.filter(conn => conn.transportMode === mode.id);
              if (modeConnections.length === 0) return null;
              
              return (
                <div key={mode.id} className="transport-group">
                  <div className="transport-group-header">
                    <span className="transport-group-title">{mode.name}</span>
                    <span className="transport-group-count">({modeConnections.length})</span>
                  </div>
                  <div className="transport-group-connections">
                    {modeConnections.map(connection => (
                      <div 
                        key={connection.id} 
                        className="connection-item"
                        style={{ backgroundColor: `${connection.color}20` }}
                      >
                        <div className="connection-info">
                          <span className="connection-single-line">
                            {connection.points[0]?.name} — {connection.points[1]?.name}{connection.duration ? ` | Duration: ${connection.duration}h` : ''}
                          </span>
                        </div>
                        <button 
                          className="remove-connection-btn"
                          onClick={() => removeConnection(connection.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Location list */}
      <LocationList 
        locations={locations}
        startId={startId}
        endId={endId}
        mode="Connection Mode"
        readOnly={true}
        showActions={false}
        allowDelete={false}
        allowSetStart={false}
        allowSetEnd={false}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        onEndIdChange={setEndId}
        showVehicleCount={false}
        showMaxCapacity={false}
      />

      {locations.length === 0 && (
        <div className="no-locations-message">
          <p>Please add location points in "Setup Path Points" mode first.</p>
        </div>
      )}

      {/* Import success toast */}
      {showImportToast && (
        <div className="import-toast">
          <div className="import-toast-content">
            {importToastMessage}
          </div>
        </div>
      )}
    </div>
  );
}
